import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  TABLO_SCENE_TRANSITION_MS,
  applyTabloClockSkew,
  formatTabloClockDisplay,
  getTabloLayerView,
  getTabloSceneEnterFrom,
  hasTabloEventLayer,
  resolveTabloLayerKey,
  resolveTabloPackScene,
  resolveTabloScoreVisible,
} from '../utilities/tabloApi';
import { useI18n } from '../utilities/i18n';

const viewFingerprint = (view) => {
  if (!view) return '';
  if (view.mode === 'srcdoc') {
    const html = String(view.html || '');
    const mid = Math.max(0, Math.floor(html.length / 2) - 32);
    // Не только head: solid/league отличаются в --accent и background глубже в документе
    return `html:${html.length}:${html.slice(0, 64)}:${html.slice(mid, mid + 64)}`;
  }
  return `src:${view.src || ''}`;
};

const IFRAME_SANDBOX = 'allow-same-origin allow-scripts';

const FrameIframe = ({ view }) => {
  if (!view) return null;
  const title = `tablo-${view.key}`;
  if (view.mode === 'srcdoc') {
    return (
      <iframe
        className="tablo-frame"
        title={title}
        srcDoc={view.html}
        sandbox={IFRAME_SANDBOX}
      />
    );
  }
  return (
    <iframe
      className="tablo-frame"
      title={title}
      src={view.src}
      sandbox={IFRAME_SANDBOX}
    />
  );
};

/** Structure key: full iframe reload only when teams/theme change — not on every pulse. */
const scoreboardStructureKey = (state) => {
  const match = state?.match || {};
  const style = state?.style || {};
  return [
    match.home_team_id,
    match.guest_team_id,
    match.home_team_short_name || match.home_team_name || '',
    match.guest_team_short_name || match.guest_team_name || '',
    style.theme || style.underlozhka || '',
    style.accent || style.accent_color || '',
  ].join('|');
};

const parseClockDisplay = (text) => {
  const m = String(text || '').trim().match(/^(\d{1,3}):(\d{2})$/);
  if (!m) return null;
  return (Number(m[1]) || 0) * 60 + (Number(m[2]) || 0);
};

const patchScoreboardDom = (iframe, state) => {
  if (!iframe) return false;
  try {
    const doc = iframe.contentDocument;
    if (!doc || !doc.body) return false;
    const match = state?.match || {};
    const nums = doc.querySelectorAll('.sb-score-num');
    if (nums[0] && match.home_team_scores != null && match.home_team_scores !== false) {
      nums[0].textContent = String(Number(match.home_team_scores) || 0);
    }
    if (nums[1] && match.guest_team_scores != null && match.guest_team_scores !== false) {
      nums[1].textContent = String(Number(match.guest_team_scores) || 0);
    }
    const clock = state?.clock;
    if (clock) {
      const el = doc.querySelector('.sb-minute');
      if (el) {
        const nextTotal = (Number(clock.minute) || 0) * 60 + (Number(clock.second) || 0);
        const shownTotal = parseClockDisplay(el.textContent);
        // Не откатывать идущие часы устаревшим snapshot (смена сцены / layers)
        const skipFalseZero = clock.running && shownTotal != null
          && nextTotal <= 1 && shownTotal > 15;
        if (!skipFalseZero) {
          el.textContent = formatTabloClockDisplay(
            Number(clock.minute) || 0,
            Number(clock.second) || 0
          );
        }
      }
    }
    return true;
  } catch (_err) {
    return false;
  }
};

/**
 * Scoreboard iframe stays mounted across event pulses.
 * React srcDoc updates remount the frame → visible blink; we set srcdoc
 * imperatively only when structure (teams/theme) changes, and patch scores in DOM.
 */
const ScoreboardIframe = ({ view, state }) => {
  const iframeRef = useRef(null);
  const structureRef = useRef('');
  const htmlRef = useRef('');

  useLayoutEffect(() => {
    if (!view || view.mode !== 'srcdoc' || !view.html) return undefined;
    const iframe = iframeRef.current;
    if (!iframe) return undefined;

    const structure = scoreboardStructureKey(state);
    const needReload = structureRef.current !== structure
      || !htmlRef.current
      || !iframe.contentDocument?.body;

    if (needReload) {
      structureRef.current = structure;
      htmlRef.current = view.html;
      const onLoad = () => {
        patchScoreboardDom(iframe, state);
      };
      iframe.addEventListener('load', onLoad);
      iframe.srcdoc = view.html;
      return () => iframe.removeEventListener('load', onLoad);
    }

    patchScoreboardDom(iframe, state);
    return undefined;
  }, [
    view?.html,
    view?.mode,
    state?.match?.home_team_scores,
    state?.match?.guest_team_scores,
    state?.match?.home_team_id,
    state?.match?.guest_team_id,
    state?.style?.theme,
    state?.style?.underlozhka,
    state?.style?.accent,
    state?.clock?.minute,
    state?.clock?.second,
  ]);

  if (!view) return null;
  if (view.mode !== 'srcdoc') {
    return (
      <iframe
        className="tablo-frame"
        title="tablo-scoreboard"
        src={view.src}
        sandbox={IFRAME_SANDBOX}
      />
    );
  }

  return (
    <iframe
      ref={iframeRef}
      className="tablo-frame"
      title="tablo-scoreboard"
      sandbox={IFRAME_SANDBOX}
    />
  );
};

/**
 * Pack-сцены анимируются в двух слотах.
 * Scoreboard всегда под pack-сценой — отдельный слой, не дублируется в event.
 * pulse event → отдельный оверлей справа (только карточка), pack/табло не трогаем.
 */
export default function TabloBoard({
  state,
  compact = false,
  eventVisible = false,
  scoreVisible: scoreVisibleProp,
}) {
  const { t } = useI18n();
  const timersRef = useRef({ raf: 0, raf2: 0, done: 0 });
  const lockRef = useRef(false);
  const activeRef = useRef('a');
  const keyRef = useRef('');
  const fpRef = useRef('');
  const pendingKeyRef = useRef('');
  const eventLeaveTimer = useRef(0);
  const eventViewRef = useRef(null);
  const stageRef = useRef(null);
  const boardClockRef = useRef({ minute: 0, second: 0, running: false });

  const [slotA, setSlotA] = useState(null);
  const [frameScale, setFrameScale] = useState(1);
  const [slotB, setSlotB] = useState(null);
  const [phase, setPhase] = useState('idle');
  const [enterFrom, setEnterFrom] = useState('fade');
  // off | prep | in | shown | leave
  const [eventPhase, setEventPhase] = useState('off');
  const [eventSlot, setEventSlot] = useState(null);

  const packScene = resolveTabloPackScene(state?.scene);
  // Pack не подменяем на event — карточка отдельно
  const layerKey = resolveTabloLayerKey(state);
  const targetView = (!state?.closed && layerKey) ? getTabloLayerView(state, layerKey) : null;
  const sceneForAnim = packScene;
  const transitionKey = targetView ? `${targetView.key}|${sceneForAnim}` : '';
  const targetFp = targetView
    ? `${viewFingerprint(targetView)}|${state?.updated_at || ''}`
    : '';

  const scoreVisible = typeof scoreVisibleProp === 'boolean'
    ? scoreVisibleProp
    : resolveTabloScoreVisible(state, true);
  const scoreView = (!state?.closed && scoreVisible)
    ? getTabloLayerView(state, 'scoreboard')
    : null;

  const liveEventView = (!state?.closed && hasTabloEventLayer(state))
    ? getTabloLayerView(state, 'event')
    : null;
  if (liveEventView) eventViewRef.current = liveEventView;

  const clearTimers = () => {
    cancelAnimationFrame(timersRef.current.raf);
    cancelAnimationFrame(timersRef.current.raf2);
    if (timersRef.current.done) clearTimeout(timersRef.current.done);
    timersRef.current = { raf: 0, raf2: 0, done: 0 };
  };

  useEffect(() => () => {
    clearTimers();
    if (eventLeaveTimer.current) clearTimeout(eventLeaveTimer.current);
  }, []);

  // В админ-превью масштабируем кадр 1920×1080 под ширину контейнера
  useEffect(() => {
    if (!compact) {
      setFrameScale(1);
      return undefined;
    }
    const el = stageRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const apply = () => {
      const w = el.clientWidth || 0;
      setFrameScale(w > 0 ? w / 1920 : 1);
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, [compact]);

  // Живые часы в HTML табло (.sb-minute) — без перезагрузки iframe.
  // Не зависим от updated_at / server_now / html: смена сцены их обновляет
  // и иначе сбрасывала бы локальный тик к устаревшему snapshot.
  useEffect(() => {
    const clock = state?.clock;
    if (!clock || state?.closed) return undefined;

    const nextMin = Number(clock.minute) || 0;
    const nextSec = Number(clock.second) || 0;
    const nextRunning = Boolean(clock.running);
    const cur = boardClockRef.current;
    const nextTotal = nextMin * 60 + nextSec;
    const curTotal = (Number(cur.minute) || 0) * 60 + (Number(cur.second) || 0);
    const falseZero = nextRunning && nextTotal <= 1 && curTotal > 15;
    if (!falseZero) {
      boardClockRef.current = { minute: nextMin, second: nextSec, running: nextRunning };
    } else {
      boardClockRef.current = { ...cur, running: nextRunning };
    }

    const writeMinute = () => {
      const root = stageRef.current;
      if (!root) return false;
      const iframe = root.querySelector('.tablo-score-layer iframe');
      if (!iframe) return false;
      try {
        const doc = iframe.contentDocument;
        const el = doc && doc.querySelector('.sb-minute');
        if (!el) return false;
        const tick = boardClockRef.current;
        el.textContent = formatTabloClockDisplay(tick.minute, tick.second);
        return true;
      } catch (_err) {
        return false;
      }
    };

    writeMinute();
    const iframe = stageRef.current && stageRef.current.querySelector('.tablo-score-layer iframe');
    const onLoad = () => { writeMinute(); };
    if (iframe) iframe.addEventListener('load', onLoad);

    // OBS/Browser Source троттлит setInterval в фоне — при возврате
    // догоняем display по server_now, не дожидаясь следующего socket-апдейта.
    const resyncVisible = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      if (falseZero) {
        writeMinute();
        return;
      }
      const skewed = applyTabloClockSkew(clock);
      if (!skewed) return;
      boardClockRef.current = {
        minute: Number(skewed.minute) || 0,
        second: Number(skewed.second) || 0,
        running: Boolean(skewed.running),
      };
      writeMinute();
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', resyncVisible);
    }

    if (!nextRunning) {
      return () => {
        if (iframe) iframe.removeEventListener('load', onLoad);
        if (typeof document !== 'undefined') {
          document.removeEventListener('visibilitychange', resyncVisible);
        }
      };
    }

    const timer = setInterval(() => {
      const tick = boardClockRef.current;
      let minute = tick.minute;
      let second = tick.second + 1;
      if (second >= 60) {
        second = 0;
        minute += 1;
      }
      boardClockRef.current = { minute, second, running: true };
      writeMinute();
    }, 1000);

    return () => {
      clearInterval(timer);
      if (iframe) iframe.removeEventListener('load', onLoad);
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', resyncVisible);
      }
    };
  }, [
    state && state.clock && state.clock.minute,
    state && state.clock && state.clock.second,
    state && state.clock && state.clock.running,
    state && state.clock && state.clock.server_now,
    state && state.closed,
  ]);

  // После смены html табло (сцена) — только перерисовать DOM из локального тика
  useEffect(() => {
    if (!scoreView?.html && !scoreView?.src) return undefined;
    const write = () => {
      const root = stageRef.current;
      if (!root) return;
      const iframe = root.querySelector('.tablo-score-layer iframe');
      if (!iframe) return;
      try {
        const el = iframe.contentDocument?.querySelector('.sb-minute');
        if (!el) return;
        const tick = boardClockRef.current;
        el.textContent = formatTabloClockDisplay(tick.minute, tick.second);
      } catch (_err) { /* ignore */ }
    };
    write();
    const iframe = stageRef.current?.querySelector('.tablo-score-layer iframe');
    if (!iframe) return undefined;
    iframe.addEventListener('load', write);
    return () => iframe.removeEventListener('load', write);
  }, [scoreView && scoreView.html, scoreView && scoreView.src]);

  // Карточка события — отдельный оверлей, не участвует в pack-слотах
  useEffect(() => {
    if (eventLeaveTimer.current) {
      clearTimeout(eventLeaveTimer.current);
      eventLeaveTimer.current = 0;
    }

    const wantShow = Boolean(eventVisible && (liveEventView || eventViewRef.current));
    if (wantShow) {
      const view = liveEventView || eventViewRef.current;
      setEventSlot(view);
      setEventPhase('prep');
      let shownTimer = 0;
      const raf = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setEventPhase('in');
          shownTimer = setTimeout(() => setEventPhase('shown'), TABLO_SCENE_TRANSITION_MS);
        });
      });
      return () => {
        cancelAnimationFrame(raf);
        if (shownTimer) clearTimeout(shownTimer);
      };
    }

    if (eventPhase === 'off' && !eventSlot) return undefined;

    setEventPhase('leave-prep');
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => setEventPhase('leave'));
    });
    eventLeaveTimer.current = setTimeout(() => {
      eventLeaveTimer.current = 0;
      setEventSlot(null);
      setEventPhase('off');
    }, TABLO_SCENE_TRANSITION_MS);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventVisible, liveEventView?.html, liveEventView?.src, state?.updated_at]);

  useEffect(() => {
    if (state?.closed) {
      clearTimers();
      lockRef.current = false;
      keyRef.current = '';
      fpRef.current = '';
      pendingKeyRef.current = '';
      activeRef.current = 'a';
      setSlotA(null);
      setSlotB(null);
      setPhase('idle');
      return;
    }

    if (!targetView || !transitionKey) {
      if (!keyRef.current) return;
      if (lockRef.current && pendingKeyRef.current === '') return;

      clearTimers();
      lockRef.current = true;
      pendingKeyRef.current = '';
      const from = 'bottom';
      const curr = activeRef.current;
      setEnterFrom(from);
      setPhase('prep');
      if (curr === 'a') {
        setSlotA((prev) => (prev ? { ...prev, role: 'leave' } : prev));
        setSlotB(null);
      } else {
        setSlotB((prev) => (prev ? { ...prev, role: 'leave' } : prev));
        setSlotA(null);
      }

      timersRef.current.raf = requestAnimationFrame(() => {
        timersRef.current.raf2 = requestAnimationFrame(() => {
          setPhase('run');
          if (curr === 'a') {
            setSlotA((prev) => (prev ? { ...prev, role: 'leave-run' } : prev));
          } else {
            setSlotB((prev) => (prev ? { ...prev, role: 'leave-run' } : prev));
          }
        });
      });

      timersRef.current.done = setTimeout(() => {
        keyRef.current = '';
        fpRef.current = '';
        pendingKeyRef.current = '';
        activeRef.current = 'a';
        setSlotA(null);
        setSlotB(null);
        setPhase('idle');
        lockRef.current = false;
        timersRef.current.done = 0;
      }, TABLO_SCENE_TRANSITION_MS);
      return;
    }

    if (!keyRef.current) {
      clearTimers();
      lockRef.current = true;
      keyRef.current = transitionKey;
      fpRef.current = targetFp;
      pendingKeyRef.current = transitionKey;
      const from = 'bottom';
      const incoming = {
        view: targetView,
        key: transitionKey,
        fingerprint: targetFp,
        role: 'enter',
      };
      activeRef.current = 'a';
      setEnterFrom(from);
      setPhase('prep');
      setSlotA(incoming);
      setSlotB(null);

      timersRef.current.raf = requestAnimationFrame(() => {
        timersRef.current.raf2 = requestAnimationFrame(() => {
          setPhase('run');
          setSlotA((prev) => (prev ? { ...prev, role: 'enter-run' } : prev));
        });
      });

      timersRef.current.done = setTimeout(() => {
        keyRef.current = pendingKeyRef.current;
        pendingKeyRef.current = '';
        activeRef.current = 'a';
        setSlotA((prev) => (prev ? { ...prev, role: 'shown' } : prev));
        setSlotB(null);
        setPhase('idle');
        lockRef.current = false;
        timersRef.current.done = 0;
      }, TABLO_SCENE_TRANSITION_MS);
      return;
    }

    if (lockRef.current && pendingKeyRef.current === transitionKey) {
      if (fpRef.current !== targetFp) {
        fpRef.current = targetFp;
        const patchIncoming = (prev) => (
          prev && prev.key === transitionKey
            ? { ...prev, view: targetView, fingerprint: targetFp }
            : prev
        );
        setSlotA(patchIncoming);
        setSlotB(patchIncoming);
      }
      return;
    }

    if (!lockRef.current && keyRef.current === transitionKey) {
      if (fpRef.current === targetFp) return;
      fpRef.current = targetFp;
      const patch = {
        view: targetView,
        key: transitionKey,
        fingerprint: targetFp,
        role: 'shown',
      };
      if (activeRef.current === 'a') setSlotA(patch);
      else setSlotB(patch);
      return;
    }

    clearTimers();
    lockRef.current = true;
    pendingKeyRef.current = transitionKey;
    const from = getTabloSceneEnterFrom(sceneForAnim);
    const curr = activeRef.current;
    const next = curr === 'a' ? 'b' : 'a';
    const incoming = {
      view: targetView,
      key: transitionKey,
      fingerprint: targetFp,
      role: 'enter',
    };

    setEnterFrom(from);
    setPhase('prep');

    if (curr === 'a') {
      setSlotA((prev) => (prev ? { ...prev, role: 'exit' } : prev));
      setSlotB(incoming);
    } else {
      setSlotB((prev) => (prev ? { ...prev, role: 'exit' } : prev));
      setSlotA(incoming);
    }

    timersRef.current.raf = requestAnimationFrame(() => {
      timersRef.current.raf2 = requestAnimationFrame(() => {
        setPhase('run');
        if (next === 'b') {
          setSlotB((prev) => (prev ? { ...prev, role: 'enter-run' } : prev));
        } else {
          setSlotA((prev) => (prev ? { ...prev, role: 'enter-run' } : prev));
        }
      });
    });

    timersRef.current.done = setTimeout(() => {
      keyRef.current = pendingKeyRef.current;
      pendingKeyRef.current = '';
      activeRef.current = next;
      if (next === 'a') {
        setSlotB(null);
        setSlotA((prev) => (prev ? { ...prev, role: 'shown' } : prev));
      } else {
        setSlotA(null);
        setSlotB((prev) => (prev ? { ...prev, role: 'shown' } : prev));
      }
      setPhase('idle');
      lockRef.current = false;
      timersRef.current.done = 0;
    }, TABLO_SCENE_TRANSITION_MS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transitionKey, targetFp, state?.closed, sceneForAnim]);

  if (state?.closed) return null;

  const hasMatch = Boolean(state?.match || state?.match_id);
  const hasLayers = Boolean(
    (state?.layers && Object.keys(state.layers).length)
    || (state?.layer_urls && Object.keys(state.layer_urls).length)
  );

  if (!hasMatch && !hasLayers) {
    return (
      <div ref={stageRef} className={`tablo-stage is-empty ${compact ? 'is-compact' : ''}`}>
        <p className="tablo-board-empty">{t('tabloWaitingState')}</p>
      </div>
    );
  }

  if (!slotA && !slotB && !targetView && !scoreView && !eventSlot) {
    return (
      <div ref={stageRef} className={`tablo-stage is-empty ${compact ? 'is-compact' : ''}`}>
        <p className="tablo-board-empty">{t('tabloWaitingState')}</p>
      </div>
    );
  }

  const renderSlot = (slot, name) => {
    if (!slot?.view) return null;
    const isIncoming = slot.role === 'enter' || slot.role === 'enter-run';
    const isLeaving = slot.role === 'leave' || slot.role === 'leave-run';
    const isOutgoing = slot.role === 'exit';
    const isShown = slot.role === 'shown';

    const cls = ['tablo-layer', `slot-${name}`];
    if (isIncoming) {
      cls.push('is-incoming', `enter-${enterFrom}`);
      if (slot.role === 'enter' || phase === 'prep') cls.push('is-prep', 'is-start');
      if (slot.role === 'enter-run') cls.push('is-in');
    } else if (isLeaving) {
      cls.push('is-outgoing', `leave-${enterFrom}`);
      if (slot.role === 'leave' || phase === 'prep') cls.push('is-prep', 'is-start');
      if (slot.role === 'leave-run') cls.push('is-out');
    } else if (isOutgoing) {
      cls.push('is-outgoing', `exit-${enterFrom}`);
      if (phase === 'prep') cls.push('is-prep');
      if (phase === 'run') cls.push('is-out');
    } else if (isShown) {
      cls.push('is-shown');
    }

    return (
      <div
        key={name}
        className={cls.join(' ')}
        data-layer={slot.view.key}
        data-role={slot.role}
      >
        <FrameIframe view={slot.view} />
      </div>
    );
  };

  const renderEventOverlay = () => {
    if (!eventSlot || eventPhase === 'off') return null;
    const cls = ['tablo-layer', 'tablo-event-layer'];
    if (eventPhase === 'prep') cls.push('is-incoming', 'enter-right', 'is-prep', 'is-start');
    else if (eventPhase === 'in') cls.push('is-incoming', 'enter-right', 'is-in');
    else if (eventPhase === 'shown') cls.push('is-shown');
    else if (eventPhase === 'leave-prep') cls.push('is-outgoing', 'leave-right', 'is-prep', 'is-start');
    else if (eventPhase === 'leave') cls.push('is-outgoing', 'leave-right', 'is-out');

    return (
      <div
        className={cls.join(' ')}
        data-layer="event"
        data-role={eventPhase}
      >
        <FrameIframe view={eventSlot} />
      </div>
    );
  };

  return (
    <div
      ref={stageRef}
      className={`tablo-stage is-frame ${compact ? 'is-compact' : ''}`}
      data-enter={enterFrom}
      data-phase={phase}
      data-score={scoreView ? (targetView ? 'under' : 'only') : (scoreVisible ? 'missing' : 'off')}
      style={{
        '--tablo-transition-ms': `${TABLO_SCENE_TRANSITION_MS}ms`,
        '--tablo-frame-scale': compact ? String(frameScale) : '1',
      }}
    >
      <div className="tablo-frame-scaler">
        <div className={`tablo-layer-stack ${phase !== 'idle' || (eventPhase !== 'off' && eventPhase !== 'shown') ? 'is-animating' : ''}`}>
          {scoreView ? (
            <div className="tablo-layer tablo-score-layer is-under-preview is-shown" data-layer="scoreboard">
              <ScoreboardIframe view={scoreView} state={state} />
            </div>
          ) : null}
          {renderSlot(slotA, 'a')}
          {renderSlot(slotB, 'b')}
          {renderEventOverlay()}
        </div>
      </div>
      {compact && scoreVisible && !scoreView ? (
        <p className="tablo-score-missing">{t('tabloScoreLayerMissing')}</p>
      ) : null}
    </div>
  );
}
